import { Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

// Ag-Grid
import {
  GridOptions,
  GridApi,
  ColDef,
  RowSelectionOptions,
  IRowNode,
  ColumnState,
  ISelectCellEditorParams,
  DomLayoutType,
  GridReadyEvent,
  GetRowIdFunc,
  GetRowIdParams,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
// Third Party
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { Subscription } from 'rxjs';
import { format } from 'date-fns';
import { NgSelectModule } from '@ng-select/ng-select';
import { AgGridAngular } from 'ag-grid-angular';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { NgxDropzoneModule } from 'ngx-dropzone';
// Services
import { SharedService } from 'src/app/shared/services/shared.service';
// Models
import { CategoryDetailsColumnDef, ItemDetailsColumnDef } from './columnDefs'; // models
// components
import { DeleteBtnComponent } from 'src/app/shared/components/ag-grid/delete-btn/delete-btn.component';
import { ProductServiceAutocompleteComponent } from 'src/app/shared/components/ag-grid/product-service-autocomplete/product-service-autocomplete.component';
import {
  CategoryDetails,
  Expense,
  FormStatus,
  ItemDetails,
  ProductServiceItem,
  Vendor,
} from 'src/app/shared/models/accounting';

import {
  ModalType,
  DatePickerConfig,
  Questions,
  Answers,
  DateFormats,
  FormActions,
  ContactTypes,
  InventoryTypes,
} from 'src/app/shared/models/constants';
import { environment } from 'src/environments/environment';
import { AccountingService } from '../services/accounting.service';
import {
  formatPhoneNumber,
  removeBlankExpenseRows,
  removeBlankSalesRows,
} from 'src/app/shared/functions';
import { findInventoryItemByName, isProduct } from '../shared/functions';

@Component({
  selector: 'app-expense',
  templateUrl: './expense.component.html',
  styleUrls: ['./expense.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, BsDatepickerModule, AgGridAngular, TooltipModule, CollapseModule, NgxDropzoneModule],
})
export class ExpenseComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  subscriptions = new Subscription();
  expense_types = { expense: 'Expense', vendor_credit: 'Vendor Credit' };
  expense_type: string = this.expense_types.expense;
  expense_action: string = FormActions.create;
  expense_id: string | null = null;
  expenseForm: FormGroup;
  edit_original: Expense = new Expense();
  categoryData: CategoryDetails[] = [];
  itemData: ItemDetails[] = [];
  total: number = 0;
  serialized_items: { upc: string; qty: number }[] = [];
  // categories = this.accountingService.categories;
  // items_db = this.accountingService.products_services;
  isDropup = true;
  vendor_added: Vendor = new Vendor();
  is_category_item: boolean = false;
  is_product_service_item: boolean = false;

  public formatPhoneNumber = formatPhoneNumber;

  categoryIsCollapsed: boolean = false;
  itemIsCollapsed: boolean = false;
  attachments: any[] = [];

  // State machine for sequentially prompting unknown product/service items during invoice parsing
  private _pendingUnknownItems: { index: number; itemObj: any }[] = [];
  private _pendingUnknownQueueIdx: number = 0;
  private _pendingParsedShippingAmount: number = 0;
  /** Tracks which itemData indices were canceled so we never set null into itemData */
  private _canceledItemIndices: Set<number> = new Set();
  // ag-grid properties Grid
  public rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    enableSelectionWithoutKeys: true,
    checkboxes: false,
  };
  domLayout: DomLayoutType = 'autoHeight';
  components: any;
  overlayLoadingTemplate;
  overlayNoRowsTemplate;
  theme = agGridDarkTheme;

  categoryGridApi!: GridApi; // Dedicated API for category grid
  itemGridApi!: GridApi; // Dedicated API for item grid

  categoryColumnDefs = CategoryDetailsColumnDef;
  categoryDefaultColDef;
  categoryCellRendererParams: any;
  categoryGridOptions: GridOptions = {};

  itemsColumnDefs = ItemDetailsColumnDef;
  itemsDefaultColDef;
  itemsCellRendererParams: any;
  itemsGridOptions: GridOptions = {};

  bsConfig: Partial<BsDatepickerConfig>;
  constructor(
    private formBuilder: FormBuilder,
    public sharedService: SharedService,
    public accountingService: AccountingService,
  ) {
    this.expenseForm = this.formBuilder.group({
      payee: ['', Validators.required],
      payment_account: ['', Validators.required],
      payment_date: [
        format(new Date(), DateFormats.M_D_Y),
        Validators.required,
      ],
      payment_method: ['', Validators.required],
      reference_number: ['', Validators.required],
      memo: ['', [Validators.maxLength(255)]],
    });
    this.itemsGridOptions = {
      getRowHeight: function (params) {
        return 30; // Set the height you want here
      },
      // Render popup cell editors (e.g. the product/service autocomplete) on
      // document.body so they aren't clipped by overflow:hidden on grid
      // wrappers or the surrounding card.
      popupParent: typeof document !== 'undefined' ? document.body : undefined,
    };
    this.categoryGridOptions = {
      getRowHeight: function (params) {
        return 30; // Set the height you want here
      },
    };
    this.categoryGridOptions.context = { ExpenseComponent: this };
    this.itemsGridOptions.context = { ExpenseComponent: this };
    this.components = {
      deleteBtn: DeleteBtnComponent,
      productServiceAutocomplete: ProductServiceAutocompleteComponent,
    };
    this.bsConfig = Object.assign({}, DatePickerConfig);
    // view_edit_item$
    this.subscriptions.add(
      this.sharedService.view_edit_item$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: Expense) => {
          this.populateForm(response);
          this.edit_original = JSON.parse(JSON.stringify(response));
        }),
    );
    // create_vendor_credit$
    this.subscriptions.add(
      this.accountingService.create_vendor_credit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          this.expense_type = 'Vendor Credit';
        }),
    );
    // option_selected$
    this.subscriptions.add(
      this.sharedService.option_selected$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (response.question === Questions.adding_inv_or_service) {
            const item = new ProductServiceItem();
            if (response.answer === 'Inventory') {
              item.name = response.optional_details['item_name'];
              item.type = 'Inventory';
              this.sharedService.openCreateProductService(
                ModalType.add,
                item,
                response.optional_details['index'],
              );
            } else if (response.answer === 'Service') {
              item.name = response.optional_details['item_name'];
              item.type = 'Service';
              this.sharedService.openCreateProductService(
                ModalType.add,
                item,
                response.optional_details['index'],
              );
            }
          }
        }),
    );
    // yes_no_submit$
    this.subscriptions.add(
      this.sharedService.yes_no_submit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.parse_invoice
          ) {
            this.parseInvoice(response.optional_details.file);
          } else if (
            response['answer'] === Answers.yes &&
            response['question'].includes(Questions.delete_expense_record)
          ) {
            this.deleteExpense();
          }
        }),
    );
    // vendor_added
    this.subscriptions.add(
      this.accountingService.vendor_list_updated$.subscribe((response) => {
        this.expenseForm.patchValue({ payee: this.vendor_added.id });
        this.vendor_added = new Vendor();
      }),
    );
    // vendor_to_add
    this.subscriptions.add(
      this.accountingService.vendor_contact_to_add$.subscribe((vendor) => {
        this.accountingService.addNewVendor(vendor);
        this.vendor_added = vendor;
      }),
    );
    // product_service_to_add$
    this.subscriptions.add(
      this.accountingService.product_service_to_add$.subscribe(
        (saveProductServiceResponse) => {
          this.accountingService.addNewProductService(
            saveProductServiceResponse.item,
          );
          for (const item of this.itemData) {
            if (!item) continue; // skip any null/undefined entries defensively
            if (item.product_service === saveProductServiceResponse.item.name) {
              item.qty = item.qty || 1;
              item.description = saveProductServiceResponse.item.description;
              item.rate = item.rate || saveProductServiceResponse.item.cost;
              item.tax = saveProductServiceResponse.item.sales_tax;
              item.amount = +(+item.qty * +item.rate).toFixed(2);
              this.itemData = [...this.itemData];
            }
          }
          // Advance the invoice-parsing unknown-item state machine (if active)
          if (this._pendingUnknownItems.length > 0) {
            this._pendingUnknownQueueIdx++;
            this._processNextUnknownItem();
          }
        },
      ),
    );
    // expense_records_list_updated$
    // this.subscriptions.add(
    //   this.accountingService.expense_records_list_updated$.subscribe(
    //     async (saveExpenseRecordResponse) => {
    //       this.sharedService.raiseSuccessToast(
    //         'Expense Record has been successfully added'
    //       );
    //       this.resetExpenseForm();
    //     }
    //   )
    // );
    // product_service_modal_canceled$
    this.subscriptions.add(
      this.accountingService.product_service_modal_canceled$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          console.log('[SM] product_service_modal_canceled$ fired. value:', response, ' pendingItems:', this._pendingUnknownItems.length, ' queueIdx:', this._pendingUnknownQueueIdx);
          // If we're in the invoice-parsing state machine, mark item for removal and advance
          if (this._pendingUnknownItems.length > 0) {
            const current = this._pendingUnknownItems[this._pendingUnknownQueueIdx];
            if (current) {
              console.log('[SM] Marking itemData index', current.index, 'as canceled (NOT nulling itemData)');
              this._canceledItemIndices.add(current.index);
            }
            this._pendingUnknownQueueIdx++;
            console.log('[SM] Advanced queue to idx:', this._pendingUnknownQueueIdx);
            this._processNextUnknownItem();
          } else {
            // Normal (non-parsing) cancel: reset the row to an empty placeholder
            const index = response;
            if (typeof index === 'number' && this.itemData[index]) {
              this.itemData[index] = new ItemDetails();
              this.itemData = [...this.itemData];
            }
          }
        }),
    );
    // cancelChoice
    this.subscriptions.add(
      this.accountingService.cancelChoice$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (response) {
            const index = response['optional_details']['index'];
            if (this.itemData[index]) {
              this.itemData[index] = new ItemDetails();
              this.itemData = [...this.itemData];
            }
          }
        }),
    );
  }

  // initialize functions
  async ngOnInit() {
    for (const column of this.categoryColumnDefs) {
      if (column['headerName'] === 'Category') {
        column['cellEditorParams'].values = [
          ...this.accountingService.categories,
        ];
      }
    }
    this.addButtonColumn();
    if (this.expense_action === FormActions.create) {
      this.addInitialGridRows();
    }
    this.calculateTotal();
    if (this.accountingService.vendor_list.length === 0) {
      this.accountingService.getVendorList();
    }
    if (this.accountingService.expense_records.length === 0) {
      await this.accountingService.getExpenseRecords();
    }

    // Keep the header validation alert in sync as the user fills the form.
    // No-op until the user has clicked Save at least once (see refreshValidationMessage).
    this.expenseForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshValidationMessage());
  }
  onCategoryGridReady(params: GridReadyEvent) {
    this.categoryGridApi = params.api;
  }

  onItemGridReady(params: GridReadyEvent) {
    this.itemGridApi = params.api;
  }
  addButtonColumn() {
    const has_edit_delete = this.categoryColumnDefs.some(
      (column) => column.headerName === 'Delete',
    );
    if (!has_edit_delete) {
      this.categoryColumnDefs.push({
        // Delete
        headerName: 'Delete',
        field: 'delete',
        resizable: true,
        width: 100,
        pinned: 'right', // pin to right
        suppressMovable: true, // make not movable
        cellRenderer: 'deleteBtn',
        cellRendererParams: {
          deleteItemClick: this.deleteCategoryItemClick.bind(this),
        },
      });
    }

    const has_edit_delete1 = this.itemsColumnDefs.some(
      (column) => column.headerName === 'Delete',
    );
    if (!has_edit_delete1) {
      this.itemsColumnDefs.push({
        // Delete
        headerName: 'Delete',
        field: 'delete',
        resizable: true,
        width: 100,
        pinned: 'right', // pin to right
        suppressMovable: true, // make not movable
        cellRenderer: 'deleteBtn',
        cellRendererParams: {
          deleteItemClick: this.deleteProductItemClick.bind(this),
        },
      });
    }
  }
  addInitialGridRows() {
    this.addItemLineClick();
    this.addItemLineClick();
    this.addCategoryLineClick();
    this.addCategoryLineClick();
  }
  populateForm(form_details: Expense) {
    this.expense_id = form_details.id;
    this.expense_action = FormActions.edit;
    this.expense_type = form_details.type;
    this.expenseForm.patchValue({
      payee: form_details.payee_id,
      payment_account: form_details.payment_account,
      payment_date: format(form_details.payment_date, DateFormats.M_D_Y),
      payment_method: form_details.payment_method,
      reference_number: form_details.reference_number,
      memo: form_details.memo,
    });
    if (form_details.item_details.length === 0) {
      this.itemIsCollapsed = true;
      this.is_product_service_item = false;
    } else {
      this.is_product_service_item = true;
      this.itemData = [...form_details.item_details];
    }
    if (form_details.category_details.length === 0) {
      this.categoryIsCollapsed = true;
      this.is_category_item = false;
    } else {
      this.is_category_item = true;
      this.categoryData = [...form_details.category_details];
    }
    this.attachments = form_details.attachments;
    this.calculateTotal();
  }

  // Category Grid Functions
  addCategoryLineClick() {
    const new_category_item: CategoryDetails = new CategoryDetails();
    new_category_item.amount = null;
    new_category_item.category = null;
    new_category_item.description = null;
    this.categoryData.push(new_category_item);
    this.categoryData = [...this.categoryData];
  }
  clearAllCategoryLinesClick() {
    this.categoryData = [
      { category: null, amount: null, description: null },
      { category: null, amount: null, description: null },
    ];
  }
  deleteCategoryItemClick(params: any) {
    this.categoryData.splice(params.rowIndex, 1);
    this.categoryData = [...this.categoryData];
    if (this.categoryData.length === 0) {
      this.is_category_item = false;
    }
    this.calculateTotal();
  }
  onCellValueChangedCategory(event): void {
    const rowIndex = event.rowIndex;
    if (event.colDef.field === 'amount' && event.newValue) {
      this.categoryData[rowIndex]['amount'] = event.data['amount'];
      this.categoryData = [...this.categoryData];
      this.is_category_item = true;
    }
    this.calculateTotal();
  }

  // Item Grid Functions
  addItemLineClick() {
    const new_item: ItemDetails = new ItemDetails();
    new_item.id = null;
    new_item.product_service = null;
    new_item.description = null;
    new_item.amount = null;
    new_item.qty = null;
    new_item.rate = null;
    new_item.tax = null;
    this.itemData.push(new_item);
    this.itemData = [...this.itemData];
  }
  clearAllItemLinesClick() {
    this.itemData = [
      {
        product_service: null,
        description: null,
        amount: null,
        qty: null,
        rate: null,
        tax: null,
      },
      {
        product_service: null,
        description: null,
        amount: null,
        qty: null,
        rate: null,
        tax: null,
      },
    ];
  }
  deleteProductItemClick(params: any) {
    this.itemData.splice(params.rowIndex, 1);
    this.itemData = [...this.itemData];
    if (this.itemData.length === 0) {
      this.is_product_service_item = false;
    }
    this.calculateTotal();
  }
  onCellValueChangedItems(event): void {
    const rowIndex = event.rowIndex;
    let new_item = this.accountingService.products_services.filter(
      (item) => item['name'] === event.data['product_service'],
    )[0];
    if (
      event.colDef.field === 'product_service' &&
      event.data['product_service']
    ) {
      if (new_item) {
        this.itemData[rowIndex]['id'] = new_item['id'];
        this.itemData[rowIndex]['product_service'] = new_item['name'];
        this.itemData[rowIndex]['description'] = new_item['description'];
        this.itemData[rowIndex]['rate'] = new_item['cost'];
        this.itemData[rowIndex]['qty'] = event.data['qty']
          ? event.data['qty']
          : 1;
        this.itemData[rowIndex]['amount'] =
          event.data['qty'] * new_item['cost'];
        this.itemData = [...this.itemData];
        this.is_product_service_item = true;
        this.isSerialItem(event.data);
      } else {
        this.itemData[rowIndex]['id'] = null;
        this.itemData[rowIndex]['description'] = null;
        this.itemData[rowIndex]['qty'] = null;
        this.itemData[rowIndex]['rate'] = null;
        this.itemData[rowIndex]['amount'] = null;
        this.itemData = [...this.itemData];
        const optional_details = {
          item_name: event.data.product_service,
          index: rowIndex,
        };
        this.sharedService.openChoiceModal(
          Questions.adding_inv_or_service,
          optional_details,
          ['Inventory', 'Service'],
        );
      }
    } else if (
      (event.colDef.field === 'qty' || event.colDef.field === 'rate') &&
      event.newValue
    ) {
      if (event.colDef.field === 'qty' && event.newValue < 0) {
        this.sharedService.raiseWarningToast('Quantity cannot be negative');
        this.itemData[rowIndex]['qty'] = 1;
        this.itemData = [...this.itemData];
        this.sharedService.raiseWarningToast('Quantity cannot be negative');
        return;
      } else {
        this.itemData[rowIndex]['qty'] = event.data['qty'];
        this.itemData[rowIndex]['rate'] = event.data['rate'];
        this.itemData[rowIndex]['amount'] =
          event.data['qty'] * event.data['rate'];
        this.itemData = [...this.itemData];
        this.isSerialItem(event.data);
      }
    }
    this.calculateTotal();
  }
  isSerialItem(input_item: any) {
    const item_details = this.accountingService.products_services.filter(
      (item) => item['upc'] === input_item['product_service'],
    )[0];
    const item_already_recorded = this.serialized_items.filter(
      (item) => item['upc'] === input_item['upc'],
    )[0];
    if (item_details['serial_item']) {
      if (item_already_recorded) {
        for (const item of this.serialized_items) {
          if (item['upc'] === input_item['upc']) {
            item['qty'] = input_item['qty'];
          }
        }
      } else {
        const serial_item = { upc: input_item['upc'], qty: input_item['qty'] };
        this.serialized_items.push(serial_item);
      }
    }
  }
  // button functions
  deleteItemClick(params: any) {
    if (params.colValues['category']) {
      this.deleteCategoryItemClick(params);
    } else if (params.colValues['product_service']) {
      this.deleteProductItemClick(params);
    }
  }
  /** Human-readable labels for the required form controls, keyed by control name. */
  private static readonly REQUIRED_FIELD_LABELS: Record<string, string> = {
    payee: 'Payee',
    payment_account: 'Payment Account',
    payment_date: 'Payment Date',
    payment_method: 'Payment Method',
    reference_number: 'Reference Number',
  };

  /** Friendly names of required fields that are still empty. Empty array
   * hides the alert. Rendered as chips in the header so each missing item
   * reads as a distinct token. */
  missing_fields: string[] = [];

  /** Names of every required field that isn't yet satisfied. The Save gate
   * also requires at least one row in either grid (matching the existing
   * is_category_item / is_product_service_item flags). */
  private computeMissingFields(): string[] {
    const missing: string[] = [];
    for (const [control, label] of Object.entries(ExpenseComponent.REQUIRED_FIELD_LABELS)) {
      if (this.expenseForm.get(control)?.invalid) missing.push(label);
    }
    if (!this.is_category_item && !this.is_product_service_item) {
      missing.push('At least one category or item');
    }
    return missing;
  }

  /** Re-evaluate the alert in response to user edits. No-op until the user
   * has clicked Save at least once — we don't want to surface the alert
   * before the user has actually attempted a save. */
  refreshValidationMessage(): void {
    if (this.missing_fields.length === 0) return;
    this.missing_fields = this.computeMissingFields();
  }

  /** Click handler for the Save / Save and Close buttons. The buttons are
   * visually disabled while the form is invalid but remain clickable so we
   * can tell the user *which* required field is missing. */
  attemptSave(closeAfter: boolean): void {
    const missing = this.computeMissingFields();
    if (missing.length > 0) {
      this.expenseForm.markAllAsTouched();
      this.missing_fields = missing;
      return;
    }

    this.missing_fields = [];
    if (closeAfter) {
      this.saveAndClose();
    } else {
      this.save();
    }
  }

  async save(form_status?: string) {
    this.itemData = removeBlankExpenseRows(this.itemData);
    this.categoryData = removeBlankExpenseRows(this.categoryData);
    if (this.verifyOneLineItem()) {
      if (!this.verifyDuplicateReference()) {
        const new_expense: Expense = new Expense();
        new_expense.id = this.expense_id;
        new_expense.type = this.expense_type;
        new_expense.payee_id = this.expenseForm.controls['payee'].value;
        new_expense.payee = this.accountingService.vendor_list.filter(
          (vendor) => vendor.id === new_expense.payee_id,
        )[0].display_name;
        new_expense.payment_account =
          this.expenseForm.controls['payment_account'].value;
        new_expense.payment_date =
          this.expenseForm.controls['payment_date'].value;
        new_expense.payment_method =
          this.expenseForm.controls['payment_method'].value;
        new_expense.reference_number =
          this.expenseForm.controls['reference_number'].value;
        new_expense.category_details = this.categoryData;
        new_expense.item_details = this.itemData;
        if (this.itemData.length > 0 && this.categoryData.length > 0) {
          new_expense.category = 'Split';
        } else if (this.itemData.length > 0 && this.categoryData.length === 0) {
          new_expense.category = 'Inventory Asset';
        } else if (this.itemData.length === 0 && this.categoryData.length > 0) {
          new_expense.category = this.categoryData[0].category;
        }
        new_expense.memo = this.expenseForm.controls['memo'].value;
        new_expense.due_date = this.expenseForm.controls['payment_date'].value;
        new_expense.balance = 0; // ------------------------------- need to add a balance field to the expense model ---------------------------------
        new_expense.total = this.total;
        new_expense.attachments = this.attachments;
        new_expense.status = new_expense.balance === 0 ? 'Paid' : 'Open';
        new_expense.status = form_status ? form_status : new_expense.status;

        if (this.expense_action === FormActions.create) {
          await this.accountingService.addNewExpenseRecord(new_expense);
        } else if (this.expense_action === FormActions.edit) {
          await this.accountingService.editExpenseRecord(new_expense);
        }
        this.sharedService.raiseSuccessToast(
          'Expense Record has been successfully added',
        );
        this.resetExpenseForm();
      }
    } else {
      this.sharedService.raiseWarningToast(
        'A line item from the Category or Product/Services is needed.',
      );
    }
  }

  saveAndClose() {
    this.save();
    this.sharedService.changeRoute('accounting-expense-table');
  }
  deleteExpenseClick() {
    this.sharedService.openYesNoModal(Questions.delete_expense_record);
  }
  deleteExpense() {
    this.save(FormStatus.deleted);
  }
  // form functions
  addNewPayee() {
    this.sharedService.openAddEditContactModal(
      ModalType.add,
      ContactTypes.vendor,
    );
  }
  editPayee() {
    const vendor = this.accountingService.vendors.find(
      (vendor) => vendor.id === this.expenseForm.get('payee').value,
    );
    this.sharedService.openAddEditContactModal(
      ModalType.edit,
      ContactTypes.vendor,
      vendor,
    );
  }
  calculateTotal() {
    this.total = 0;
    for (const item of this.itemData) {
      if (item['amount']) {
        this.total = +this.total + +item['amount'];
      }
    }
    for (const item of this.categoryData) {
      if (item['amount']) {
        this.total = +this.total + +item['amount'];
      }
    }
    if (this.expense_type === 'Vendor Credit') {
      this.total = -this.total;
    }
    this.total = Number(this.total.toFixed(2));
    // Pick up grid mutations (added/removed/edited line items) for the
    // header validation alert. Cheap no-op when the alert isn't visible.
    this.refreshValidationMessage();
  }
  addAttachment(event: any) {
    if (+event.addedFiles[0]['size'] < 20000000) {
      const file = event.addedFiles[0];
      const isDuplicate = this.attachments.some(existingFile => existingFile.name === file.name && existingFile.size === file.size);
      
      if (isDuplicate) {
        this.sharedService.raiseWarningToast('File is already attached.');
        return;
      }

      if (this.attachments.length < 2) {
        this.attachments.push(...event.addedFiles);
        
        // Ask if they want to parse the first file
        this.sharedService.openYesNoModal(
          Questions.parse_invoice,
          { file }
        );
      } else {
        this.sharedService.raiseWarningToast(
          'You can only attach 2 files to an expense record',
        );
      }
    } else {
      this.sharedService.raiseWarningToast(
        'File size is too large. Please upload a file less than 20MB',
      );
    }
  }

  onInvoiceSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const isDuplicate = this.attachments.some(existingFile => existingFile.name === file.name && existingFile.size === file.size);
      if (isDuplicate) {
        this.sharedService.raiseWarningToast('File is already attached.');
        event.target.value = null;
        return;
      }

      if (+file.size < 20000000) {
        if (this.attachments.length < 2) {
          this.attachments.push(file);
        }
        // Direct parse without the Yes/No prompt since they clicked "Parse Invoice"
        this.parseInvoice(file);
      } else {
        this.sharedService.raiseWarningToast(
          'File size is too large. Please upload a file less than 20MB',
        );
      }
    }
    // Clear the input so the same file can be selected again if needed
    event.target.value = null;
  }

  async parseInvoice(file: File) {
    const formData = new FormData();
    formData.append('invoice', file);
    this.sharedService.openStatusSpinner('Parsing Invoice');
    try {

      const response = await fetch(`${environment.apiUrl}/expense_records/parse-invoice`, {
        method: 'POST',
        body: formData,
        credentials: 'include' // Important for sending the httpOnly session cookie
      });

      if (!response.ok) {
        throw new Error('Failed to parse invoice');
      }

      const { parsedData } = await response.json();
      
      this.sharedService.closeStatusSpinner();
      
      let matchedVendorId = null;
      if (parsedData.payee) {
        // Find matching vendor ID or leave blank to let user add it
        const parsedPayeeClean = parsedData.payee.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        const matchingVendor = this.accountingService.vendors.find(v => {
          const vendorNameClean = (v.contact?.display_name || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
          if (!vendorNameClean || !parsedPayeeClean) return false;
          // Exact alphanumeric match, OR one contains the other (e.g., 'rsrgroup' matches 'rsrgroupinc')
          return vendorNameClean === parsedPayeeClean || 
                 vendorNameClean.includes(parsedPayeeClean) || 
                 parsedPayeeClean.includes(vendorNameClean);
        });
        if (matchingVendor) {
          matchedVendorId = matchingVendor.id;
          this.expenseForm.patchValue({ payee: matchedVendorId });
        } else {
          this.expenseForm.patchValue({ payee: null });
          this.sharedService.raiseWarningToast('Payee not found in system. Prefilling contact details...');
          
          const prefilledVendor = new Vendor();
          prefilledVendor.contact.company_name = parsedData.payee;
          prefilledVendor.contact.display_name = parsedData.payee;
          if (parsedData.vendor_address) {
            prefilledVendor.address.street = parsedData.vendor_address.street;
            prefilledVendor.address.street2 = parsedData.vendor_address.street2;
            prefilledVendor.address.city = parsedData.vendor_address.city;
            prefilledVendor.address.state = parsedData.vendor_address.state;
            prefilledVendor.address.zip_code = parsedData.vendor_address.zip_code;
            prefilledVendor.address.country = parsedData.vendor_address.country;
          }

          this.sharedService.openAddEditContactModal(
            ModalType.add,
            ContactTypes.vendor,
            prefilledVendor
          );
          
          await new Promise<void>(resolve => {
              const sub1 = this.accountingService.vendor_contact_to_add$.subscribe(() => { cleanup(); resolve(); });
              const sub2 = this.accountingService.vendor_contact_modal_closed$.subscribe(() => { cleanup(); resolve(); });
              
              const cleanup = () => {
                  sub1.unsubscribe();
                  sub2.unsubscribe();
              };
          });
        }
      }

      if (parsedData.payment_date) {
        const [year, month, day] = parsedData.payment_date.split('-');
        if (year && month && day) {
          this.expenseForm.patchValue({ payment_date: `${month}/${day}/${year}` });
        } else {
          this.expenseForm.patchValue({ payment_date: new Date(parsedData.payment_date) });
        }
      }

      if (parsedData.payment_method) {
        const methods = this.sharedService.company_info?.accounting_options?.payment_methods || [];
        const parsedMethod = parsedData.payment_method.trim().toLowerCase();
        const matchedMethod = methods.find((m: string) => m.toLowerCase() === parsedMethod) || parsedData.payment_method.trim();
        this.expenseForm.patchValue({ payment_method: matchedMethod });
      }

      if (parsedData.invoice_number) {
        const dupReference = parsedData.invoice_number.trim();
        if (dupReference) {
          const hasDuplicate = this.accountingService.expense_records.some(
            exp => (exp.reference_number || '').trim().toLowerCase() === dupReference.toLowerCase() && exp.status !== FormStatus.deleted
          );
          if (hasDuplicate) {
            this.sharedService.openMessageModal(`An expense with Reference Number "${dupReference}" already exists!`);
          }
        }
        this.expenseForm.patchValue({ reference_number: parsedData.invoice_number });
      }

      // Populate grid with category details
      if (parsedData.category_details && parsedData.category_details.length > 0) {
        this.clearAllCategoryLinesClick(); // Clear default empty row
        this.categoryData = parsedData.category_details.map((cat: any) => {
          return {
            category: cat.category,
            description: cat.description,
            amount: typeof cat.amount === 'object' ? cat.amount.amount : cat.amount
          };
        });
      }

      // Populate grid with line items
      if (parsedData.line_items && parsedData.line_items.length > 0) {
        this.clearAllItemLinesClick(); // Clear default empty row
        
        let unknownItems: any[] = [];
        // Standard UPC pattern — 10 to 14 numeric digits
        const isNumericUpc = (v: string) => /^\d{10,14}$/.test(v);
        
        this.itemData = parsedData.line_items.map((item: any, idx: number) => {
          let matchedUpc = null;
          if (item.upc) {
            const found = this.accountingService.products_services.find(
              ps => ps.upc === item.upc || ps.name === item.upc
            );
            if (found) matchedUpc = found.name; // In AG Grid, we use the name to map
          }

          if (!matchedUpc) {
            // If the UPC was positively resolved from vendor_sku_mappings (valid
            // 10-14 digit numeric UPC) but the product doesn't exist yet in our
            // system, place it directly in the grid — the user can create the
            // product/service later. We skip the modal for these because we
            // already know the correct UPC; asking the user to confirm it adds
            // no value and breaks the flow for bulk-import invoices.
            const hasResolvedUpc = item.upc && isNumericUpc(item.upc);

            if (hasResolvedUpc) {
              // Drop straight into the grid with the resolved UPC pre-filled.
              // The "Add Product Item" modal is NOT opened for these rows.
            } else {
              // Truly unknown item (no valid UPC) — prompt the user.
              const newPs = new ProductServiceItem();
              newPs.type = InventoryTypes.inventory;
              newPs.name = item.upc || item.description;
              newPs.upc = item.upc || '';
              newPs.description = item.description || '';
              const rawPrice = typeof item.price === 'object' ? item.price.amount : item.price;
              newPs.sale_price_rate = null as any;
              newPs.cost = parseFloat(rawPrice || 0).toFixed(2) as any;
              newPs.income_asset_account = 'INVENTORY ASSET';
              newPs.income_account = 'MERCHANDISE SALES';
              newPs.expense_account = 'COST OF GOODS SOLD';
              newPs.inventory_asset_account = 'INVENTORY ASSET';
              newPs.sales_tax = true;
              newPs.preferred_vendor = matchedVendorId;
              unknownItems.push({ index: idx, itemObj: newPs });
            }
          }

          return {
            product_service: matchedUpc || item.upc || item.description,
            description: item.description,
            qty: item.qty,
            rate: typeof item.price === 'object' ? item.price.amount : item.price,
            amount: typeof item.amount === 'object' ? item.amount.amount : item.amount
          };
        });
        
        // Show success toast
        this.sharedService.raiseSuccessToast('Invoice parsed successfully!');

        if (unknownItems.length > 0) {
          // Kick off the state-machine that sequentially opens a modal for each unknown item.
          // Completion is handled inside _processNextUnknownItem().
          this._pendingUnknownItems = unknownItems;
          this._pendingUnknownQueueIdx = 0;
          this._pendingParsedShippingAmount = parsedData.shipping_amount || 0;
          this._processNextUnknownItem();
        } else {
          this._finalizeInvoiceParsing(parsedData.shipping_amount || 0);
        }
      } else {
        this.calculateTotal();
        this.sharedService.raiseSuccessToast('Invoice parsed successfully!');
      }

    } catch (error) {
      console.error(error);
      this.sharedService.raiseWarningToast('Could not parse invoice.');
    } finally {
      this.sharedService.closeStatusSpinner();
    }
  }

  /**
   * State-machine driver for sequentially prompting the user to add unknown
   * product/service items encountered during invoice parsing. Called from:
   *  - parseInvoice() to start the queue
   *  - product_service_to_add$ subscriber when an item is saved
   *  - product_service_modal_canceled$ subscriber when a modal is dismissed
   */
  private _processNextUnknownItem(): void {
    console.log('[SM] _processNextUnknownItem called. idx:', this._pendingUnknownQueueIdx, '/ total:', this._pendingUnknownItems.length);
    if (this._pendingUnknownQueueIdx < this._pendingUnknownItems.length) {
      const next = this._pendingUnknownItems[this._pendingUnknownQueueIdx];
      console.log('[SM] Opening modal for item', this._pendingUnknownQueueIdx, ':', next.itemObj?.name, 'at itemData index', next.index);
      this.sharedService.openCreateProductService(ModalType.add, next.itemObj, next.index);
    } else {
      // All items handled — finalize the grid and clear the state machine
      console.log('[SM] Queue complete. Finalizing invoice parsing.');
      this._finalizeInvoiceParsing(this._pendingParsedShippingAmount);
      this._pendingUnknownItems = [];
      this._pendingUnknownQueueIdx = 0;
      this._pendingParsedShippingAmount = 0;
    }
  }

  /** Populates the item and category grids after all unknown items are resolved. */
  private _finalizeInvoiceParsing(shippingAmount: number): void {
    // Filter out any items the user canceled during the modal sequence
    this.itemData = this.itemData.filter((_item, idx) => !this._canceledItemIndices.has(idx));
    this._canceledItemIndices.clear();

    this.is_product_service_item = true;
    this.itemIsCollapsed = false;
    setTimeout(() => {
      this.itemGridApi?.setGridOption('rowData', this.itemData);
    }, 100);

    if (this.categoryData && this.categoryData.length > 0) {
      this.is_category_item = true;
      this.categoryIsCollapsed = false;
      setTimeout(() => {
        if (this.categoryGridApi) {
          this.categoryGridApi.setGridOption('rowData', this.categoryData);
        }
      }, 100);
    } else if (shippingAmount > 0) {
      this.categoryData[0] = {
        category: 'Shipping',
        description: 'Shipping Cost',
        amount: shippingAmount
      };
      this.categoryData = [...this.categoryData];
      this.is_category_item = true;
      this.categoryIsCollapsed = false;
      setTimeout(() => {
        if (this.categoryGridApi) {
          this.categoryGridApi.setGridOption('rowData', this.categoryData);
        }
      }, 100);
    }

    this.calculateTotal();
  }

  removeAttachment(file: any) {
    console.log(file);
    this.attachments.splice(this.attachments.indexOf(event), 1);
  }
  verifyDuplicateReference(): boolean {
    const input_value = this.expenseForm.controls['reference_number'].value;
    if (
      this.expense_action === FormActions.edit &&
      input_value === this.edit_original.reference_number
    ) {
      this.expenseForm.controls['reference_number'].setErrors(null);
      return false;
    } else {
      const duplicate_reference = this.accountingService.expense_records.find(
        (record) => record.reference_number === input_value,
      );
      if (duplicate_reference) {
        this.sharedService.raiseWarningToast(
          'This reference number is already being used.',
        );
        this.expenseForm.controls['reference_number'].setErrors({
          duplicate: true,
        });
        return true;
      } else {
        this.expenseForm.controls['reference_number'].setErrors(null);
        return false;
      }
    }
  }
  verifyOneLineItem(): boolean {
    if (this.categoryData[0]?.amount || this.itemData[0]?.amount) {
      return true;
    } else {
      return false;
    }
  }
  resetExpenseForm() {
    this.clearAllItemLinesClick();
    this.clearAllCategoryLinesClick();
    this.expenseForm.reset();
    this.total = 0;
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
